import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Cell
} from 'recharts';
import { 
  Target, TrendingUp, TrendingDown, Users, DollarSign,
  AlertTriangle, RefreshCw, Loader2, CheckCircle2, Clock,
  Building, Gauge, Lightbulb, ChevronRight, ArrowRight,
  Flag, Milestone, Shield, Heart, Award, Rocket, AlertCircle,
  Calendar, MapPin, Briefcase, BarChart3, PieChart, Bell, Trophy, Zap, Upload, Download,
  Database, Sparkles
} from 'lucide-react';
import { 
  PageHeader, SectionHeader, ModernCard,
  modernTabsClass, modernTabTriggerClass
} from '@/components/ModernUI';
import {
  MilestoneBadge,
  PhaseProgressBadge,
  AlertCountBadge,
  MilestoneAlertCard,
  showMilestoneToast,
  MILESTONES
} from '@/components/MilestoneAlerts';
import { InsightsExport } from '@/components/InsightsExport';
import { DataHistoryPanel } from '@/components/DataHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase colors
const phaseColors = {
  phase_1: { bg: 'bg-slate-500', text: 'text-slate-600', gradient: 'from-slate-500 to-slate-600' },
  phase_2: { bg: 'bg-blue-500', text: 'text-blue-600', gradient: 'from-blue-500 to-blue-600' },
  phase_3: { bg: 'bg-violet-500', text: 'text-[#00b0ab]', gradient: 'from-violet-500 to-violet-600' },
  phase_4: { bg: 'bg-amber-500', text: 'text-amber-600', gradient: 'from-amber-500 to-amber-600' },
  phase_5: { bg: 'bg-emerald-500', text: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600' }
};

// Priority badge colors
const priorityColors = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-amber-100 text-amber-700 border-amber-200',
  MEDIUM: 'bg-blue-100 text-blue-700 border-blue-200',
  STRATEGIC: 'bg-violet-100 text-violet-700 border-violet-200'
};

// Phase Timeline Component with Milestone Badges
const PhaseTimeline = ({ phases, currentPhase, currentAdc }) => {
  return (
    <div className="relative" data-testid="phase-timeline">
      {/* Timeline line */}
      <div className="absolute top-6 left-0 right-0 h-1 bg-slate-200 dark:bg-slate-700" />
      
      <div className="relative flex justify-between">
        {phases.map((phase, idx) => {
          const isComplete = phase.status === 'completed';
          const isCurrent = phase.id === currentPhase;
          const colors = phaseColors[phase.id];
          const progressToPhase = currentAdc ? Math.min(100, (currentAdc / phase.adc_target) * 100) : 0;
          const isApproaching = isCurrent && progressToPhase >= 85 && progressToPhase < 100;
          
          // Determine milestone status for badge
          let milestoneStatus = 'upcoming';
          if (isComplete) milestoneStatus = 'achieved';
          else if (isCurrent && isApproaching) milestoneStatus = 'approaching';
          else if (isCurrent) milestoneStatus = 'in_progress';
          
          return (
            <div key={phase.id} className="flex flex-col items-center" style={{ width: '18%' }}>
              {/* Phase node */}
              <div 
                className={`
                  relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                  transition-all duration-300
                  ${isComplete ? `${colors.bg} text-white` : 
                    isCurrent ? `${colors.bg} text-white ring-4 ring-offset-2 ring-${colors.bg.replace('bg-', '')}` : 
                    'bg-slate-200 dark:bg-slate-700 text-slate-500'}
                  ${isApproaching ? 'animate-pulse' : ''}
                `}
              >
                {isComplete ? (
                  <Trophy className="h-6 w-6" />
                ) : isApproaching ? (
                  <Zap className="h-6 w-6" />
                ) : (
                  <span className="font-bold">{phase.year}</span>
                )}
              </div>
              
              {/* Phase label */}
              <div className="mt-3 text-center">
                <p className={`font-semibold text-sm ${isCurrent ? colors.text : 'text-slate-600 dark:text-slate-400'}`}>
                  {phase.name.split(' ')[0]}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {phase.adc_target} ADC
                </p>
                {/* Milestone Status Badge */}
                <div className="mt-1.5">
                  <MilestoneBadge status={milestoneStatus} size="sm" showIcon={false} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Gap Analysis Card
const GapAnalysisCard = ({ gap, title, icon: Icon, color }) => {
  const isPositiveGap = gap.gap_to_phase <= 0;
  
  return (
    <Card className="h-full">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${color}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <h4 className="font-semibold text-sm">{title}</h4>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Current</span>
            <span className="font-mono font-bold">{gap.current || gap.current_adc || gap.current_rncm || gap.current_monthly_revenue?.toLocaleString() || gap.current_compliance}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Phase Target</span>
            <span className="font-mono">{gap.phase_target?.toLocaleString?.() || gap.phase_target}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-sm text-muted-foreground">Gap</span>
            <Badge className={isPositiveGap ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {isPositiveGap ? '✓ On Target' : gap.gap_to_phase}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recommendation Card
const RecommendationCard = ({ rec }) => {
  return (
    <Card className="hover:shadow-lg transition-all" data-testid="recommendation-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Badge className={`${priorityColors[rec.priority]} border`}>{rec.priority}</Badge>
          <Badge variant="outline">{rec.category}</Badge>
        </div>
        <CardTitle className="text-base mt-2">{rec.strategic_goal}</CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {rec.actions?.map((action, idx) => (
            <AccordionItem key={idx} value={`action-${idx}`}>
              <AccordionTrigger className="text-sm font-medium hover:no-underline">
                {action.action}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <div>
                    <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Tactics</h5>
                    <ul className="space-y-1">
                      {action.tactics?.map((tactic, tidx) => (
                        <li key={tidx} className="flex items-start gap-2 text-sm">
                          <ChevronRight className="h-4 w-4 text-[#00b0ab] mt-0.5 flex-shrink-0" />
                          {tactic}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {action.timeline}
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {action.expected_impact}
                    </Badge>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        
        {rec.kpi_targets && (
          <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">KPI Targets</h5>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(rec.kpi_targets).slice(0, 3).map(([key, value]) => (
                <div key={key} className="text-center">
                  <p className="text-xs text-muted-foreground">{key.replace(/_/g, ' ')}</p>
                  <p className="font-mono font-bold text-sm">{typeof value === 'number' && value < 1 ? `${(value * 100).toFixed(0)}%` : value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {rec.risk_if_not_addressed && (
          <div className="mt-3 flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-400">{rec.risk_if_not_addressed}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Risk Card
const RiskCard = ({ risk }) => {
  const severityColors = {
    HIGH: 'bg-red-500',
    MEDIUM: 'bg-amber-500',
    LOW: 'bg-blue-500'
  };
  
  return (
    <Card className="border-l-4" style={{ borderLeftColor: risk.severity === 'HIGH' ? '#EF4444' : risk.severity === 'MEDIUM' ? '#F59E0B' : '#3B82F6' }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <Badge variant="outline">{risk.category}</Badge>
          <div className="flex gap-1">
            <Badge className={`${severityColors[risk.severity]} text-white text-xs`}>{risk.severity}</Badge>
            <Badge variant="outline" className="text-xs">{risk.likelihood}</Badge>
          </div>
        </div>
        <p className="font-medium text-sm mb-2">{risk.risk}</p>
        <div className="space-y-2">
          <div>
            <p className="text-xs text-muted-foreground">Mitigation:</p>
            <p className="text-sm">{risk.mitigation}</p>
          </div>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">Impact if realized:</p>
            <p className="text-sm text-red-600 dark:text-red-400">{risk.impact_if_realized}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
export default function StrategicPlanDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [scorecardData, setScorecardData] = useState(null);
  const [milestoneAlerts, setMilestoneAlerts] = useState([]);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [aiStrategistData, setAiStrategistData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [growthData, setGrowthData] = useState(null);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState('');
  const chatEndRef = React.useRef(null);

  // Generate milestone alerts based on strategic data
  const generateMilestoneAlerts = useCallback((data) => {
    if (!data) return [];
    
    const alerts = [];
    const { current_performance, gap_analysis, trajectory } = data;
    const currentAdc = current_performance?.adc || 50;
    const currentPhase = current_performance?.phase || 'phase_1';
    const milestone = MILESTONES[currentPhase];
    
    if (!milestone) return alerts;
    
    const progressToPhase = (currentAdc / milestone.adc_target) * 100;
    
    // Approaching milestone (90-99%)
    if (progressToPhase >= 90 && progressToPhase < 100) {
      alerts.push({
        id: `approaching-${currentPhase}`,
        type: 'milestone_approaching',
        title: `Approaching ${milestone.name}!`,
        message: `Only ${milestone.adc_target - currentAdc} ADC away from achieving this milestone!`,
        priority: 'high',
        icon: Zap,
        color: 'amber'
      });
    }
    
    // Milestone achieved
    if (currentAdc >= milestone.adc_target) {
      alerts.push({
        id: `achieved-${currentPhase}`,
        type: 'milestone_achieved',
        title: `${milestone.name} Achieved!`,
        message: `Congratulations! You've reached ${milestone.adc_target} ADC.`,
        priority: 'high',
        icon: Trophy,
        color: 'emerald'
      });
    }
    
    // Trajectory at risk
    if (trajectory?.on_track === false) {
      alerts.push({
        id: 'trajectory-risk',
        type: 'at_risk',
        title: 'Growth Trajectory Alert',
        message: `Current growth (${trajectory.current_net_growth}/week) below required (${trajectory.required_weekly_growth}/week)`,
        priority: 'high',
        icon: AlertTriangle,
        color: 'red'
      });
    } else if (trajectory?.assessment === 'Exceeding Plan') {
      alerts.push({
        id: 'trajectory-exceeding',
        type: 'positive',
        title: 'Exceeding Growth Targets!',
        message: `Net growth of ${trajectory.current_net_growth}/week exceeds requirements!`,
        priority: 'medium',
        icon: Rocket,
        color: 'emerald'
      });
    }
    
    // Workforce gap
    if (gap_analysis?.workforce?.gap_to_phase > 3) {
      alerts.push({
        id: 'workforce-gap',
        type: 'warning',
        title: 'Staffing Gap Detected',
        message: `Need ${gap_analysis.workforce.gap_to_phase} more RNCMs to support growth`,
        priority: 'medium',
        icon: Users,
        color: 'amber'
      });
    }
    
    return alerts;
  }, []);

  // Fetch strategic overview
  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [overviewRes, scorecardRes] = await Promise.all([
        fetch(`${API_URL}/api/strategic-plan/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/strategic-plan/scorecard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (overviewRes.ok && scorecardRes.ok) {
        const [overview, scorecard] = await Promise.all([
          overviewRes.json(),
          scorecardRes.json()
        ]);
        setOverviewData(overview);
        setScorecardData(scorecard);
        
        // Generate and show milestone alerts
        const alerts = generateMilestoneAlerts(overview);
        setMilestoneAlerts(alerts);
        
        // Show toast notifications for high priority alerts on first load
        if (!isRefresh && alerts.length > 0) {
          alerts
            .filter(a => a.priority === 'high')
            .slice(0, 2)
            .forEach((alert, idx) => {
              setTimeout(() => {
                const Icon = alert.icon;
                if (alert.type === 'milestone_achieved' || alert.type === 'positive') {
                  toast.success(alert.title, { 
                    description: alert.message,
                    duration: 6000,
                    icon: <Icon className="h-5 w-5" />
                  });
                } else if (alert.type === 'milestone_approaching') {
                  toast(alert.title, { 
                    description: alert.message,
                    duration: 6000,
                    icon: <Icon className="h-5 w-5 text-amber-500" />
                  });
                } else if (alert.type === 'at_risk') {
                  toast.warning(alert.title, { 
                    description: alert.message,
                    duration: 6000,
                    icon: <Icon className="h-5 w-5" />
                  });
                }
              }, idx * 1500);
            });
        }
        
        if (isRefresh) toast.success('Data refreshed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error loading strategic plan data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch AI Strategist Executive Report
  const fetchAiStrategistReport = async (forceRefresh = false) => {
    if (!token) return;
    setLoadingAudit(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-strategist/executive-report?force_refresh=${forceRefresh}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAiStrategistData(data.report);
        if (forceRefresh) {
          toast.success('Executive audit report generated');
        }
      }
    } catch (error) {
      console.error('AI Strategist Error:', error);
      toast.error('Error loading executive audit');
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Load AI Strategist data when tab is activated
  useEffect(() => {
    if (activeTab === 'executive-audit' && !aiStrategistData) {
      fetchAiStrategistReport();
    }
    if (activeTab === 'growth-market') {
      if (!growthData && !growthLoading) fetchGrowthData();
    }
  }, [activeTab, token]);

  const fetchGrowthData = async () => {
    setGrowthLoading(true);
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      const [planRes, marketRes, partnersRes, actionRes, oppsRes] = await Promise.all([
        fetch(`${API_URL}/api/growth-market/strategic-plan`, { headers: authHeaders }),
        fetch(`${API_URL}/api/growth-market/market-data`, { headers: authHeaders }),
        fetch(`${API_URL}/api/growth-market/partners`, { headers: authHeaders }),
        fetch(`${API_URL}/api/growth-market/action-plan`, { headers: authHeaders }),
        fetch(`${API_URL}/api/growth-market/opportunities`, { headers: authHeaders }),
      ]);
      const [plan, market, partners, action, opps] = await Promise.all([
        planRes.json(), marketRes.json(), partnersRes.json(), actionRes.json(), oppsRes.json()
      ]);
      setGrowthData({ plan, market, partners, action, opportunities: opps });
      // Also fetch pivot intelligence
      try {
        const pivotRes = await fetch(`${API_URL}/api/growth-intel/pivot-intelligence`, { headers: authHeaders });
        if (pivotRes.ok) {
          const pivotData = await pivotRes.json();
          setGrowthData(prev => ({ ...prev, pivot: pivotData }));
        }
      } catch (e) { console.error('Pivot fetch failed:', e); }
    } catch (e) {
      console.error('Failed to fetch growth data:', e);
    } finally {
      setGrowthLoading(false);
    }
  };

  const sendChatMessage = async (msg) => {
    const message = msg || chatInput;
    if (!message.trim()) return;
    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', content: message, timestamp: new Date().toISOString() }]);
    setChatInput('');
    try {
      const res = await fetch(`${API_URL}/api/growth-strategist/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, session_id: chatSessionId }),
      });
      const data = await res.json();
      if (data.response) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
        if (data.session_id) setChatSessionId(data.session_id);
      }
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Failed to get response. Please try again.', timestamp: new Date().toISOString() }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // Resource projection chart data
  const resourceChartData = useMemo(() => {
    if (!overviewData?.resource_projections?.phase_projections) return [];
    return overviewData.resource_projections.phase_projections.map(p => ({
      phase: `Year ${p.year}`,
      adc: p.adc,
      rncm: p.rncm_needed,
      revenue: p.estimated_revenue / 1000000
    }));
  }, [overviewData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="loading">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  const { current_performance, gap_analysis, trajectory, recommendations, risk_assessment } = overviewData || {};
  const alertCount = milestoneAlerts.filter(a => a.priority === 'high').length;

  return (
    <div className="space-y-6" data-testid="strategic-plan-dashboard">
      {/* Header */}
      <PageHeader
        icon={Target}
        title="Strategic Plan Dashboard"
        subtitle="5-Year Growth Plan: Scaling Excellence from 170 to 450 ADC"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        <div className="flex items-center gap-2">
          {/* Milestone Alerts Button with Badge */}
          <div className="relative">
            <Button 
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => setShowAlertsPanel(!showAlertsPanel)}
              data-testid="alerts-toggle-btn"
            >
              <Bell className="h-4 w-4 mr-2" />
              Alerts
            </Button>
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-red-500 text-white">
                {alertCount}
              </span>
            )}
          </div>
          
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </PageHeader>

      {/* Milestone Alerts Panel */}
      {showAlertsPanel && milestoneAlerts.length > 0 && (
        <Card className="border-2 border-violet-200 dark:border-violet-800 animate-in slide-in-from-top-2" data-testid="milestone-alerts-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-violet-500" />
              Milestone Alerts
              <Badge variant="secondary" className="ml-2">{milestoneAlerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {milestoneAlerts.map(alert => {
                const Icon = alert.icon;
                const colorClasses = {
                  emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200',
                  amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200',
                  red: 'bg-red-50 dark:bg-red-900/20 border-red-200',
                  blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200'
                };
                const iconColors = {
                  emerald: 'text-emerald-600',
                  amber: 'text-amber-600',
                  red: 'text-red-600',
                  blue: 'text-blue-600'
                };
                
                return (
                  <div 
                    key={alert.id}
                    className={`p-3 rounded-lg border ${colorClasses[alert.color] || colorClasses.blue}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className={`h-5 w-5 mt-0.5 ${iconColors[alert.color] || iconColors.blue}`} />
                      <div>
                        <h4 className="font-semibold text-sm">{alert.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase Progress Badge - Quick Status */}
      {current_performance && (
        <div className="flex items-center justify-between">
          <PhaseProgressBadge 
            currentAdc={current_performance.adc}
            phaseTarget={MILESTONES[current_performance.phase]?.adc_target || 170}
            phaseName={MILESTONES[current_performance.phase]?.name?.split(' ')[0] || 'Foundation'}
          />
          {trajectory?.on_track ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <TrendingUp className="h-3 w-3 mr-1" />
              On Track for 5-Year Goal
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {trajectory?.assessment || 'Behind Schedule'}
            </Badge>
          )}
        </div>
      )}

      {/* Vision & Mission */}
      <Card className="bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border-violet-200 dark:border-violet-800">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-2">
                <Flag className="h-4 w-4" /> Vision
              </h3>
              <p className="text-sm mt-1">{overviewData?.vision}</p>
            </div>
            <div>
              <h3 className="font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                <Rocket className="h-4 w-4" /> Mission
              </h3>
              <p className="text-sm mt-1">{overviewData?.mission}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ModernCard>
          <CardContent className="p-4 text-center">
            <Gauge className="h-8 w-8 mx-auto text-[#00b0ab] mb-2" />
            <p className="text-sm text-muted-foreground">Overall Progress</p>
            <p className="text-3xl font-bold text-[#00b0ab]">{current_performance?.progress_pct || 0}%</p>
            <Progress value={current_performance?.progress_pct || 0} className="h-2 mt-2" />
          </CardContent>
        </ModernCard>
        
        <ModernCard>
          <CardContent className="p-4 text-center">
            <Building className="h-8 w-8 mx-auto text-[#00b0ab] mb-2" />
            <p className="text-sm text-muted-foreground">Current ADC</p>
            <p className="text-3xl font-bold text-[#00b0ab]">{current_performance?.adc || 50}</p>
            <p className="text-xs text-muted-foreground mt-1">Target: 450</p>
          </CardContent>
        </ModernCard>
        
        <ModernCard>
          <CardContent className="p-4 text-center">
            <Milestone className="h-8 w-8 mx-auto text-amber-600 mb-2" />
            <p className="text-sm text-muted-foreground">Current Phase</p>
            <p className="text-xl font-bold text-amber-600">{current_performance?.phase_name?.split(' ')[0]}</p>
            <Badge className="mt-1">{current_performance?.phase}</Badge>
          </CardContent>
        </ModernCard>
        
        <ModernCard>
          <CardContent className="p-4 text-center">
            {trajectory?.on_track ? (
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
            ) : (
              <AlertCircle className="h-8 w-8 mx-auto text-amber-600 mb-2" />
            )}
            <p className="text-sm text-muted-foreground">Trajectory</p>
            <p className={`text-xl font-bold ${trajectory?.on_track ? 'text-emerald-600' : 'text-amber-600'}`}>
              {trajectory?.assessment}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {trajectory?.years_to_450 ? `${trajectory.years_to_450} yrs to goal` : 'N/A'}
            </p>
          </CardContent>
        </ModernCard>
      </div>

      {/* Phase Timeline */}
      <ModernCard>
        <CardHeader>
          <SectionHeader icon={Calendar} title="5-Year Strategic Phases" subtitle="Progress through each phase milestone" />
        </CardHeader>
        <CardContent>
          <PhaseTimeline 
            phases={overviewData?.phases || []} 
            currentPhase={current_performance?.phase}
            currentAdc={current_performance?.adc}
          />
        </CardContent>
      </ModernCard>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <BarChart3 className="h-4 w-4 mr-2" /> Gap Analysis
          </TabsTrigger>
          <TabsTrigger value="insights" className={modernTabTriggerClass}>
            <Lightbulb className="h-4 w-4 mr-2" /> Insights
          </TabsTrigger>
          <TabsTrigger value="predictions" className={modernTabTriggerClass}>
            <TrendingUp className="h-4 w-4 mr-2" /> Predictions
          </TabsTrigger>
          <TabsTrigger value="recommendations" className={modernTabTriggerClass}>
            <Target className="h-4 w-4 mr-2" /> Recommendations
          </TabsTrigger>
          <TabsTrigger value="resources" className={modernTabTriggerClass}>
            <Briefcase className="h-4 w-4 mr-2" /> Resources
          </TabsTrigger>
          <TabsTrigger value="risks" className={modernTabTriggerClass}>
            <Shield className="h-4 w-4 mr-2" /> Risks
          </TabsTrigger>
          <TabsTrigger value="scorecard" className={modernTabTriggerClass}>
            <Award className="h-4 w-4 mr-2" /> Scorecard
          </TabsTrigger>
          <TabsTrigger value="executive-audit" className={modernTabTriggerClass}>
            <Gauge className="h-4 w-4 mr-2" /> Executive Audit
          </TabsTrigger>
          <TabsTrigger value="history" className={modernTabTriggerClass}>
            <Clock className="h-4 w-4 mr-2" /> History
          </TabsTrigger>
          <TabsTrigger value="growth-market" className={modernTabTriggerClass}>
            <TrendingUp className="h-4 w-4 mr-2" /> Growth & Market
          </TabsTrigger>
        </TabsList>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-6" data-testid="insights-tab">
          <div className="flex items-center justify-between">
            <SectionHeader 
              icon={Lightbulb} 
              title="Strategic Insights" 
              subtitle="Data-driven analysis based on current system metrics"
            />
            <InsightsExport 
              insights={(overviewData?.insights || []).map(i => ({
                title: i.title || i.category,
                description: i.message || i.insight,
                severity: i.type === 'risk' ? 'critical' : i.type === 'growth' || i.type === 'positive' ? 'positive' : 'info',
                category: i.domain || 'Strategic',
                recommendation: i.action || i.recommendation
              }))} 
              pageTitle="Strategic Plan" 
            />
          </div>
          
          {/* Current Metrics Summary Cards */}
          {overviewData?.current_metrics_summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#00b0ab]/15 dark:bg-indigo-900/30">
                      <Users className="h-5 w-5 text-[#00b0ab]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Census</p>
                      <p className="text-2xl font-bold">{overviewData.current_metrics_summary.census?.adc?.toFixed(0) || 0}</p>
                      <p className="text-xs text-muted-foreground">
                        +{overviewData.current_metrics_summary.census?.weekly_admissions || 0} / 
                        -{overviewData.current_metrics_summary.census?.weekly_discharges || 0} weekly
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
              
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                      <Users className="h-5 w-5 text-[#00b0ab]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Workforce</p>
                      <p className="text-2xl font-bold">{overviewData.current_metrics_summary.workforce?.rncm_count || 0} RNCMs</p>
                      <p className="text-xs text-muted-foreground">
                        {overviewData.current_metrics_summary.workforce?.staff_utilization?.toFixed(1) || 0}% utilization
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
              
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Revenue</p>
                      <p className="text-2xl font-bold">
                        ${((overviewData.current_metrics_summary.financial?.monthly_revenue || 0) / 1000000).toFixed(2)}M
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {overviewData.current_metrics_summary.financial?.net_margin?.toFixed(1) || 0}% margin
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
              
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                      <Heart className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Quality</p>
                      <p className="text-2xl font-bold">{overviewData.current_metrics_summary.quality?.pain_control?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-muted-foreground">
                        Pain Control | {overviewData.current_metrics_summary.quality?.compliance_rate?.toFixed(1) || 0}% Compliance
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
            </div>
          )}
          
          {/* Insights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {overviewData?.insights?.map((insight, idx) => {
              const colorMap = {
                growth: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: 'text-emerald-600' },
                positive: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: 'text-emerald-600' },
                risk: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200', icon: 'text-red-600' },
                recommendation: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200', icon: 'text-blue-600' }
              };
              const colors = colorMap[insight.type] || colorMap.recommendation;
              const IconComponent = insight.type === 'risk' ? AlertTriangle : 
                                   insight.type === 'positive' || insight.type === 'growth' ? TrendingUp : 
                                   Lightbulb;
              
              return (
                <Card 
                  key={insight.id || idx} 
                  className={`${colors.bg} ${colors.border} border`}
                  data-testid={`insight-${insight.domain?.toLowerCase() || idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <IconComponent className={`h-5 w-5 mt-0.5 ${colors.icon}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{insight.title}</h4>
                          <Badge variant="outline" className="text-xs">{insight.domain}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            className={
                              insight.impact === 'positive' ? 'bg-emerald-100 text-emerald-700' :
                              insight.impact === 'high' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }
                          >
                            Impact: {insight.impact}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {(!overviewData?.insights || overviewData.insights.length === 0) && (
              <Card className="col-span-2">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No insights available. Add data to generate strategic insights.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="space-y-6" data-testid="predictions-tab">
          <SectionHeader 
            icon={TrendingUp} 
            title="Strategic Predictions" 
            subtitle="4-week and 12-week forecasts based on current trends"
          />
          
          {/* Predictions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overviewData?.predictions?.map((pred, idx) => {
              const isPositive = pred.trend === 'up' || 
                               (pred.metric === 'Burnout Risk (Staff)' && pred.trend === 'down');
              const trendIcon = pred.trend === 'up' ? TrendingUp : 
                               pred.trend === 'down' ? TrendingDown : 
                               Gauge;
              const TrendIcon = trendIcon;
              
              return (
                <ModernCard key={idx} data-testid={`prediction-${pred.metric?.replace(/\s+/g, '-').toLowerCase()}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm">{pred.metric}</h4>
                      <div className="flex items-center gap-1.5">
                        {pred.source === 'census_forecasting_service' && (
                          <Badge 
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-5 bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800"
                            title="AI-powered forecasting with trend analysis"
                          >
                            <Sparkles className="h-3 w-3 mr-0.5" />
                            AI
                          </Badge>
                        )}
                        <Badge 
                          className={
                            pred.confidence >= 85 ? 'bg-emerald-100 text-emerald-700' :
                            pred.confidence >= 75 ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }
                        >
                          {pred.confidence}% conf
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Current</p>
                        <p className="text-2xl font-bold">{pred.current}</p>
                      </div>
                      
                      <TrendIcon 
                        className={`h-6 w-6 ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`} 
                      />
                      
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">4-Week Forecast</p>
                        <p className={`text-2xl font-bold ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {pred.forecast_4w || pred.forecast || pred.required_for_adc || '-'}
                        </p>
                      </div>
                    </div>
                    
                    {pred.target && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Target</span>
                          <span className="font-medium">{pred.target}</span>
                        </div>
                        <Progress 
                          value={Math.min(100, (pred.current / pred.target) * 100)} 
                          className="h-1.5 mt-1"
                        />
                      </div>
                    )}
                    
                    {pred.phase1_target && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Phase 1 Target</span>
                          <span className="font-medium">{pred.phase1_target}</span>
                        </div>
                        <Progress 
                          value={Math.min(100, (pred.current / pred.phase1_target) * 100)} 
                          className="h-1.5 mt-1"
                        />
                      </div>
                    )}
                    
                    {pred.gap !== undefined && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Hiring Gap</span>
                          <span className={`font-medium ${pred.gap > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {pred.gap > 0 ? `+${pred.gap} needed` : 'Adequate'}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </ModernCard>
              );
            })}
            
            {(!overviewData?.predictions || overviewData.predictions.length === 0) && (
              <Card className="col-span-3">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No predictions available. Add data to generate forecasts.</p>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Milestone Progress */}
          {overviewData?.milestones?.length > 0 && (
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Flag} title="Milestone Progress" subtitle="Progress toward next strategic milestone" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {overviewData.milestones.map((milestone, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Flag className={`h-4 w-4 ${milestone.status === 'on_track' ? 'text-emerald-600' : 'text-amber-600'}`} />
                          <span className="font-medium">{milestone.name}</span>
                        </div>
                        <Badge 
                          className={milestone.status === 'on_track' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
                        >
                          {milestone.status === 'on_track' ? 'On Track' : 'At Risk'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span>Current: <strong>{milestone.current}</strong></span>
                        <span>Target: <strong>{milestone.target}</strong></span>
                        <span>Progress: <strong>{milestone.progress_pct}%</strong></span>
                        {milestone.weeks_to_achieve !== 'N/A' && (
                          <span className="text-muted-foreground">~{milestone.weeks_to_achieve} weeks</span>
                        )}
                      </div>
                      <Progress value={milestone.progress_pct} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Gap Analysis Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GapAnalysisCard 
              gap={gap_analysis?.census || {}} 
              title="Census" 
              icon={Building} 
              color="from-indigo-500 to-blue-600"
            />
            <GapAnalysisCard 
              gap={gap_analysis?.workforce || {}} 
              title="Workforce" 
              icon={Users} 
              color="from-violet-500 to-purple-600"
            />
            <GapAnalysisCard 
              gap={gap_analysis?.financial || {}} 
              title="Financial" 
              icon={DollarSign} 
              color="from-emerald-500 to-teal-600"
            />
            <GapAnalysisCard 
              gap={gap_analysis?.quality || {}} 
              title="Quality" 
              icon={Heart} 
              color="from-rose-500 to-pink-600"
            />
          </div>
          
          {/* Overall Gap Assessment */}
          <ModernCard>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">Overall Gap Assessment</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Distance from current phase targets
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">{gap_analysis?.overall_gap_score || 0}%</p>
                  <Badge className={
                    gap_analysis?.assessment === 'On Track' ? 'bg-emerald-100 text-emerald-700' :
                    gap_analysis?.assessment === 'Minor Gap' ? 'bg-blue-100 text-blue-700' :
                    gap_analysis?.assessment === 'Significant Gap' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {gap_analysis?.assessment}
                  </Badge>
                </div>
              </div>
              <Progress value={100 - (gap_analysis?.overall_gap_score || 0)} className="h-3 mt-4" />
            </CardContent>
          </ModernCard>

          {/* Trajectory Details */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={TrendingUp} title="Growth Trajectory" subtitle="Projected path to 450 ADC goal" />
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Current Net Growth</p>
                  <p className={`text-3xl font-bold ${(trajectory?.current_net_growth || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {trajectory?.current_net_growth >= 0 ? '+' : ''}{trajectory?.current_net_growth}/week
                  </p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Required Growth</p>
                  <p className="text-3xl font-bold text-[#00b0ab]">+{trajectory?.required_weekly_growth}/week</p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Growth Deficit</p>
                  <p className={`text-3xl font-bold ${(trajectory?.growth_deficit || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {trajectory?.growth_deficit > 0 ? trajectory.growth_deficit : 'None'}
                  </p>
                </div>
              </div>
              
              {trajectory?.milestone_projections?.length > 0 && (
                <div className="mt-6">
                  <h5 className="font-semibold text-sm mb-3">Milestone Projections</h5>
                  <div className="space-y-2">
                    {trajectory.milestone_projections.map((m, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{m.phase}</Badge>
                          <span className="font-medium">{m.target_adc} ADC</span>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm">{m.weeks_to_reach} weeks</p>
                          <p className="text-xs text-muted-foreground">{m.projected_date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(recommendations || []).map((rec, idx) => (
              <RecommendationCard key={idx} rec={rec} />
            ))}
          </div>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="space-y-6">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={BarChart3} title="Resource Projections by Phase" />
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={resourceChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="phase" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.75rem'
                    }} 
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="adc" name="ADC" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="rncm" name="RNCMs Needed" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="revenue" name="Revenue ($M)" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </ModernCard>

          {/* Investment Summary */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={DollarSign} title="5-Year Investment Summary" />
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {Object.entries(overviewData?.resource_projections?.investment_summary || {}).map(([key, value]) => (
                  <div key={key} className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p className="text-2xl font-bold text-emerald-600">${(value / 1000000).toFixed(2)}M</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="space-y-6">
          <ModernCard>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">Overall Risk Level</h4>
                  <p className="text-sm text-muted-foreground">Based on gap analysis and strategic assessment</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">{risk_assessment?.overall_risk_score || 0}%</p>
                  <Badge className={
                    risk_assessment?.risk_level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                    risk_assessment?.risk_level === 'MODERATE' ? 'bg-blue-100 text-blue-700' :
                    risk_assessment?.risk_level === 'ELEVATED' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {risk_assessment?.risk_level}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </ModernCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(risk_assessment?.risks || []).map((risk, idx) => (
              <RiskCard key={idx} risk={risk} />
            ))}
          </div>
        </TabsContent>

        {/* Scorecard Tab */}
        <TabsContent value="scorecard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Census Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Building} title="Census Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.census && Object.entries(scorecardData.census).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: {data.target}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{typeof data.current === 'number' && data.current < 1 ? `${(data.current * 100).toFixed(1)}%` : data.current}</p>
                      {data.progress_pct !== undefined && (
                        <Progress value={data.progress_pct} className="h-1.5 w-20 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>

            {/* Workforce Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Users} title="Workforce Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.workforce && Object.entries(scorecardData.workforce).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: {data.target}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{data.current}</p>
                      {data.gap !== undefined && data.gap > 0 && (
                        <Badge variant="outline" className="text-xs">Gap: {data.gap}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>

            {/* Financial Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={DollarSign} title="Financial Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.financial && Object.entries(scorecardData.financial).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: ${(data.target / 1000000).toFixed(1)}M</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">${(data.current / 1000000).toFixed(2)}M</p>
                      {data.progress_pct !== undefined && (
                        <Progress value={Math.min(100, data.progress_pct)} className="h-1.5 w-20 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>

            {/* Quality Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Heart} title="Quality Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.quality && Object.entries(scorecardData.quality).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: {data.target}%</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{data.current}%</p>
                      <Badge className={data.status === 'excellent' || data.status === 'good' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {data.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>
          </div>
        </TabsContent>

        {/* Executive Audit Tab - AI Strategist */}
        <TabsContent value="executive-audit" className="space-y-6" data-testid="executive-audit-tab">
          <div className="flex items-center justify-between">
            <SectionHeader 
              icon={Gauge} 
              title="AI Executive Performance Audit" 
              subtitle="8-Pillar strategic analysis with predictive trajectory"
            />
            <Button 
              onClick={() => fetchAiStrategistReport(true)} 
              disabled={loadingAudit}
              variant="outline"
            >
              {loadingAudit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate New Report
                </>
              )}
            </Button>
          </div>

          {loadingAudit && !aiStrategistData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
            </div>
          ) : aiStrategistData ? (
            <>
              {/* Executive Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Organizational Health Score */}
                <ModernCard className="relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-1 h-full ${
                    aiStrategistData.organizational_health?.color === 'green' ? 'bg-emerald-500' :
                    aiStrategistData.organizational_health?.color === 'yellow' ? 'bg-amber-500' :
                    aiStrategistData.organizational_health?.color === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                  }`} />
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Health Score</span>
                      <Badge className={
                        aiStrategistData.organizational_health?.grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                        aiStrategistData.organizational_health?.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        Grade {aiStrategistData.organizational_health?.grade}
                      </Badge>
                    </div>
                    <p className="text-3xl font-bold">{aiStrategistData.organizational_health?.composite_score?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{aiStrategistData.organizational_health?.category}</p>
                  </CardContent>
                </ModernCard>

                {/* Strategic Phase */}
                <ModernCard>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Strategic Phase</span>
                      <Badge className={
                        aiStrategistData.strategic_phase?.alignment_status === 'On Track' || 
                        aiStrategistData.strategic_phase?.alignment_status === 'Ahead of Plan' 
                          ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }>
                        {aiStrategistData.strategic_phase?.alignment_status}
                      </Badge>
                    </div>
                    <p className="text-xl font-bold">{aiStrategistData.strategic_phase?.current_phase?.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Year {aiStrategistData.strategic_phase?.current_phase?.year} of 5-Year Plan
                    </p>
                  </CardContent>
                </ModernCard>

                {/* Year 5 Probability */}
                <ModernCard>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">450 ADC by 2030</span>
                      <Target className="h-5 w-5 text-violet-500" />
                    </div>
                    <p className={`text-2xl font-bold ${
                      aiStrategistData.trajectory?.year_5_projection?.probability === 'High' ? 'text-emerald-600' :
                      aiStrategistData.trajectory?.year_5_projection?.probability === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {aiStrategistData.trajectory?.year_5_projection?.probability} Probability
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Projected: {aiStrategistData.trajectory?.year_5_projection?.projected_adc} ADC
                    </p>
                  </CardContent>
                </ModernCard>

                {/* Primary Constraint */}
                <ModernCard>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Primary Constraint</span>
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                    </div>
                    <p className="text-lg font-bold">{aiStrategistData.trajectory?.primary_constraint?.area}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {aiStrategistData.trajectory?.primary_constraint?.issue}
                    </p>
                  </CardContent>
                </ModernCard>
              </div>

              {/* 8-Pillar Strategic Grades */}
              <ModernCard>
                <CardHeader>
                  <SectionHeader icon={Award} title="8 Strategic Pillar Grades" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {aiStrategistData.pillar_grades && Object.entries(aiStrategistData.pillar_grades).map(([key, pillar]) => (
                      <div key={key} className={`p-4 rounded-lg border ${
                        pillar.grade === 'A' ? 'border-emerald-200 bg-emerald-50/50' :
                        pillar.grade === 'B' ? 'border-blue-200 bg-blue-50/50' :
                        pillar.grade === 'C' ? 'border-amber-200 bg-amber-50/50' :
                        pillar.grade === 'D' ? 'border-orange-200 bg-orange-50/50' :
                        'border-red-200 bg-red-50/50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-2xl font-bold ${
                            pillar.grade === 'A' ? 'text-emerald-600' :
                            pillar.grade === 'B' ? 'text-blue-600' :
                            pillar.grade === 'C' ? 'text-amber-600' :
                            pillar.grade === 'D' ? 'text-orange-600' : 'text-red-600'
                          }`}>{pillar.grade}</span>
                          <span className="text-sm text-muted-foreground">{pillar.score}/100</span>
                        </div>
                        <p className="text-sm font-medium mb-2">{pillar.pillar}</p>
                        {pillar.issues?.length > 0 && (
                          <div className="space-y-1">
                            {pillar.issues.slice(0, 2).map((issue, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  issue.severity === 'HIGH' ? 'bg-red-500' :
                                  issue.severity === 'MEDIUM' ? 'bg-amber-500' : 'bg-blue-500'
                                }`} />
                                <span className="text-xs text-muted-foreground truncate">{issue.flag}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </ModernCard>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Risk Indices */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Shield} title="Risk Indices" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {aiStrategistData.risk_indices && Object.entries(aiStrategistData.risk_indices).map(([key, risk]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className={`text-lg font-bold ${
                            risk.level === 'Low' || risk.level === 'Strong' ? 'text-emerald-600' :
                            risk.level === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>{risk.level}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">{risk.score}</div>
                          <Progress 
                            value={risk.score} 
                            className={`h-2 w-20 ${
                              risk.level === 'Low' || risk.level === 'Strong' ? '[&>div]:bg-emerald-500' :
                              risk.level === 'Moderate' ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
                            }`} 
                          />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </ModernCard>

                {/* Executive Priorities */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Target} title="Executive Priorities This Quarter" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {aiStrategistData.executive_priorities?.map((priority, idx) => (
                      <div key={idx} className="p-4 border rounded-lg bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-800/30">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-amber-500' : 'bg-blue-500'
                          }`}>{priority.priority}</span>
                          <span className="font-semibold">{priority.focus}</span>
                        </div>
                        <p className="text-sm text-muted-foreground ml-11">{priority.rationale}</p>
                        <p className="text-sm mt-2 ml-11 text-[#00b0ab] dark:text-violet-400">
                          <ArrowRight className="inline h-4 w-4 mr-1" />
                          {priority.action}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </ModernCard>
              </div>

              {/* Trajectory Forecasts */}
              <ModernCard>
                <CardHeader>
                  <SectionHeader icon={TrendingUp} title="Predictive Trajectory" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">Current ADC</p>
                      <p className="text-3xl font-bold">{aiStrategistData.trajectory?.current_adc}</p>
                    </div>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">6-Month Forecast</p>
                      <p className="text-3xl font-bold text-blue-600">{aiStrategistData.trajectory?.forecast_6_months?.adc}</p>
                      <p className="text-xs text-muted-foreground">{aiStrategistData.trajectory?.forecast_6_months?.date}</p>
                    </div>
                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">12-Month Forecast</p>
                      <p className="text-3xl font-bold text-[#00b0ab]">{aiStrategistData.trajectory?.forecast_12_months?.adc}</p>
                      <p className="text-xs text-muted-foreground">{aiStrategistData.trajectory?.forecast_12_months?.date}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">2030 Target</p>
                      <p className="text-3xl font-bold text-emerald-600">{aiStrategistData.trajectory?.year_5_projection?.target_adc}</p>
                      <p className="text-xs text-muted-foreground">Projected: {aiStrategistData.trajectory?.year_5_projection?.projected_adc}</p>
                    </div>
                  </div>
                  
                  {/* Financial Trajectory */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                      Financial Trajectory to $70-90M
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Current Annual</p>
                        <p className="text-xl font-bold">{aiStrategistData.trajectory?.financial_trajectory?.current_annual_revenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Projected 2030</p>
                        <p className="text-xl font-bold text-emerald-600">{aiStrategistData.trajectory?.financial_trajectory?.projected_2030_revenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Target</p>
                        <p className="text-xl font-bold">{aiStrategistData.trajectory?.financial_trajectory?.target_revenue}</p>
                        <Badge className={aiStrategistData.trajectory?.financial_trajectory?.on_track ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {aiStrategistData.trajectory?.financial_trajectory?.on_track ? 'On Track' : 'At Risk'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>

              {/* Acceleration Plan */}
              {aiStrategistData.acceleration_plan && (
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Rocket} title="Acceleration Plan to 450 ADC" />
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Staffing Model</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                            <span>Current RNCMs</span>
                            <span className="font-bold">{aiStrategistData.acceleration_plan.structural_upgrades?.staffing_model?.current_rncm}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                            <span>Required RNCMs</span>
                            <span className="font-bold">{aiStrategistData.acceleration_plan.structural_upgrades?.staffing_model?.required_rncm}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                            <span>Additional Hires Needed</span>
                            <span className="font-bold text-emerald-600">{aiStrategistData.acceleration_plan.structural_upgrades?.staffing_model?.additional_hires}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Capital Requirements</h4>
                        <div className="space-y-2 text-sm">
                          {aiStrategistData.acceleration_plan.structural_upgrades?.capital_requirements && 
                            Object.entries(aiStrategistData.acceleration_plan.structural_upgrades.capital_requirements).map(([key, value]) => (
                              <div key={key} className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                                <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="font-bold">{value}</span>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Centralization Needs</h4>
                        <ul className="space-y-2">
                          {aiStrategistData.acceleration_plan.structural_upgrades?.centralization_needs?.map((need, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              {need}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">AI Integration Opportunities</h4>
                        <ul className="space-y-2">
                          {aiStrategistData.acceleration_plan.structural_upgrades?.ai_integration?.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <Zap className="h-4 w-4 text-violet-500" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </ModernCard>
              )}

              {/* Report Timestamp */}
              <p className="text-xs text-muted-foreground text-center">
                Report generated: {new Date(aiStrategistData.report_generated_at).toLocaleString()}
              </p>
            </>
          ) : (
            <div className="text-center py-12">
              <Gauge className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Click "Generate New Report" to run the AI Executive Audit</p>
            </div>
          )}
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="history" className="space-y-6">
          <SectionHeader 
            icon={Clock} 
            title="Strategic Data History" 
            subtitle="View timeline of data entries related to strategic planning"
          />
          <div className="grid gap-4">
            <DataHistoryPanel 
              category="census" 
              title="Census & Growth Data" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="program" 
              title="Program Overview" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="financial" 
              title="Financial Metrics" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="workforce" 
              title="Workforce Data" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
          </div>
        </TabsContent>

        {/* Growth & Market Strategy Tab */}
        <TabsContent value="growth-market" className="space-y-6" data-testid="growth-market-tab">
          {growthLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : growthData ? (
            <>
              {/* Phase Progress + Action Plan */}
              <Card className="bg-slate-900/80 border-slate-700/50 overflow-hidden">
                <div className="p-6 border-l-4 border-cyan-500 bg-cyan-500/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">5-Year Growth Trajectory: {growthData.plan?.service_area}</h3>
                      <p className="text-slate-400 mt-1">Current ADC: {growthData.action?.current_adc} | Phase: {growthData.action?.phase_label} | Target: {growthData.action?.target_adc} ADC | Gap: {growthData.action?.adc_gap}</p>
                    </div>
                    <Badge className="bg-cyan-500/20 text-cyan-400 text-lg px-4 py-1">{growthData.action?.revenue_target}</Badge>
                  </div>
                  <div className="grid grid-cols-5 gap-2 mt-4">
                    {Object.entries(growthData.plan?.phases || {}).map(([key, phase]) => {
                      const isCurrent = growthData.action?.current_phase === key;
                      return (
                        <div key={key} className={`p-3 rounded-lg text-center ${isCurrent ? 'bg-cyan-500/20 border-2 border-cyan-400' : 'bg-slate-800/50 border border-slate-700'}`}>
                          <p className="text-xs text-slate-400">{key.replace('_', ' ').toUpperCase()}</p>
                          <p className={`text-2xl font-black ${isCurrent ? 'text-cyan-400' : 'text-white'}`}>{phase.target_adc}</p>
                          <p className="text-xs text-slate-500">{phase.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>


              {/* Pivot Intelligence — Auto-generated from entered data */}
              {growthData?.pivot?.pivot_recommendations?.length > 0 && (
                <Card className="bg-slate-900/80 border-slate-700/50 overflow-hidden">
                  <div className="p-4 bg-gradient-to-r from-amber-500/10 to-red-500/10 border-b border-amber-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-amber-400" />
                        <h3 className="text-lg font-bold text-white">Strategic Pivot Intelligence</h3>
                        <Badge className="bg-amber-500/20 text-amber-400 text-xs">Auto-generated from your data</Badge>
                      </div>
                      <div className="text-right text-xs text-slate-400">
                        {growthData.pivot.current_state?.clinicians} nurses at {growthData.pivot.current_state?.utilization}% util | Spare capacity: {growthData.pivot.current_state?.spare_patient_capacity} patients
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4 space-y-3">
                    {/* Signals */}
                    {growthData.pivot.signals?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {growthData.pivot.signals.map((s, i) => (
                          <Badge key={i} className={
                            s.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            s.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            s.severity === 'positive' ? 'bg-emerald-500/20 text-emerald-400' :
                            s.severity === 'opportunity' ? 'bg-cyan-500/20 text-cyan-400' :
                            'bg-amber-500/20 text-amber-400'
                          }>{s.signal}</Badge>
                        ))}
                      </div>
                    )}
                    {/* Pivot Recommendations */}
                    {growthData.pivot.pivot_recommendations?.map((p, i) => (
                      <div key={i} className={`p-4 rounded-lg border ${
                        p.priority === 1 ? 'bg-red-500/5 border-red-500/20' :
                        p.priority === 2 ? 'bg-amber-500/5 border-amber-500/20' :
                        'bg-slate-800/50 border-slate-700/50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-bold text-sm">{p.pivot}</h4>
                          <div className="flex gap-2">
                            <Badge className="bg-slate-700 text-slate-300 text-xs">{p.timeline}</Badge>
                            <Badge className={p.priority === 1 ? 'bg-red-500/20 text-red-400' : p.priority === 2 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}>P{p.priority}</Badge>
                          </div>
                        </div>
                        <p className="text-xs text-slate-300 mb-2">{p.why}</p>
                        <ul className="space-y-1 mb-2">
                          {p.actions?.slice(0, 3).map((a, j) => (
                            <li key={j} className="text-xs text-slate-400 flex items-start gap-1.5">
                              <span className="text-emerald-400 mt-0.5">&#8226;</span> {a}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-cyan-400/70 italic">{p.data_connection?.slice(0, 120)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}


              {/* AI Growth Strategist Chat */}
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Zap className="h-5 w-5 text-violet-400" />
                    AI Growth Strategist
                    <Badge variant="outline" className="border-violet-500/30 text-violet-400 text-xs ml-2">Knows your full context</Badge>
                  </CardTitle>
                  <p className="text-xs text-slate-400 mt-1">Describe your situation — the AI knows your ADC ({growthData?.action?.current_adc}), all 43 partners, NOVA market data, and strategic plan.</p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[
                      "Our admissions dropped 20% — what should we do?",
                      "How to approach Inova Loudoun for preferred provider?",
                      "Plan to open a satellite in Loudoun County",
                      "We need 10 more admissions/week to hit target",
                      "How to compete with Capital Caring?",
                      "Build a 90-day referral growth plan",
                    ].map((prompt, i) => (
                      <button key={i} onClick={() => sendChatMessage(prompt)} disabled={chatLoading}
                        className="text-xs px-3 py-1.5 rounded-full bg-slate-800 border border-slate-600 text-slate-400 hover:border-violet-500/50 hover:text-violet-400 transition-colors disabled:opacity-50">
                        {prompt}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-[400px] overflow-y-auto space-y-3 mb-3 px-1">
                    {chatMessages.length === 0 && (
                      <div className="text-center py-6 text-slate-500">
                        <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Tell me your situation and I will build a specific plan</p>
                      </div>
                    )}
                    {chatMessages.map((msg, i) => (
                      <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'assistant' && <div className="p-2 rounded-lg bg-violet-500/20 h-fit flex-shrink-0 mt-1"><Zap className="h-4 w-4 text-violet-400" /></div>}
                        <div className={`rounded-xl p-4 max-w-[85%] ${msg.role === 'user' ? 'bg-cyan-500/15 border border-cyan-500/20' : 'bg-slate-800/80 border border-slate-700/50'}`}>
                          <div className="text-sm text-slate-200 whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex gap-3">
                        <div className="p-2 rounded-lg bg-violet-500/20"><Zap className="h-4 w-4 text-violet-400" /></div>
                        <div className="bg-slate-800/80 rounded-xl px-5 py-3 border border-slate-700/50"><Loader2 className="h-4 w-4 animate-spin text-violet-400" /></div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="flex gap-2">
                    <input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                      placeholder="Describe your situation, challenge, or question..."
                      className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
                      disabled={chatLoading} />
                    <label className="px-3 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg cursor-pointer transition-colors flex items-center gap-1">
                      <Upload className="h-4 w-4" />
                      <input type="file" className="hidden" accept=".csv,.xlsx,.xls,.pdf,.txt" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setChatLoading(true);
                        setChatMessages(prev => [...prev, { role: 'user', content: `Uploaded: ${f.name} — Analyze this data and tell me what to do`, timestamp: new Date().toISOString() }]);
                        try {
                          const formData = new FormData();
                          formData.append('file', f);
                          formData.append('question', 'Analyze this data and tell me what to do for our growth strategy');
                          formData.append('session_id', chatSessionId);
                          const res = await fetch(`${API_URL}/api/growth-strategist/analyze-upload`, {
                            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
                          });
                          const data = await res.json();
                          if (data.analysis) {
                            setChatMessages(prev => [...prev, { role: 'assistant', content: data.analysis, timestamp: new Date().toISOString(), canExport: true }]);
                          }
                        } catch { setChatMessages(prev => [...prev, { role: 'assistant', content: 'Failed to analyze file', timestamp: new Date().toISOString() }]); }
                        finally { setChatLoading(false); setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); }
                        e.target.value = '';
                      }} />
                    </label>
                    <button onClick={() => sendChatMessage()} disabled={!chatInput.trim() || chatLoading}
                      className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:from-violet-400 hover:to-purple-500">
                      {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
                    </button>
                  </div>
                  
                  {/* Export Actions — only show when there are AI responses */}
                  {chatMessages.some(m => m.role === 'assistant' && m.content.length > 50) && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-700">
                      <button onClick={() => {
                        const lastAI = [...chatMessages].reverse().find(m => m.role === 'assistant');
                        if (!lastAI) return;
                        fetch(`${API_URL}/api/growth-strategist/generate-pdf`, {
                          method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ content: lastAI.content, title: 'Growth Strategy Report' })
                        }).then(r => r.json()).then(data => {
                          if (data.html) {
                            const w = window.open('', '_blank');
                            w.document.write(data.html); w.document.close();
                            setTimeout(() => w.print(), 500);
                          }
                        });
                      }} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/20 transition-colors">
                        <Download className="h-3.5 w-3.5" /> Export PDF for Leadership
                      </button>
                      <button onClick={() => {
                        const email = prompt('Enter recipient email(s), separated by commas:');
                        if (!email) return;
                        const lastAI = [...chatMessages].reverse().find(m => m.role === 'assistant');
                        if (!lastAI) return;
                        fetch(`${API_URL}/api/growth-strategist/send-email`, {
                          method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ recipients: email.split(',').map(e => e.trim()), content: lastAI.content, subject: 'Growth Strategy Report — Goodwin Hospice' })
                        }).then(r => r.json()).then(data => {
                          if (data.success) alert('Email sent successfully!');
                          else alert('Failed to send email');
                        });
                      }} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20 transition-colors">
                        <Bell className="h-3.5 w-3.5" /> Email to Leadership
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Immediate Actions */}
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Target className="h-5 w-5 text-red-400" />
                    Immediate Action Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {growthData.action?.immediate_actions?.map((a, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${
                        a.impact === 'critical' ? 'bg-red-500/10 border-red-500/20' :
                        a.impact === 'very_high' ? 'bg-orange-500/10 border-orange-500/20' :
                        a.impact === 'high' ? 'bg-amber-500/10 border-amber-500/20' :
                        'bg-slate-800/50 border-slate-700'
                      }`}>
                        <div>
                          <p className="text-white text-sm font-medium">{a.action}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{a.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-slate-700 text-slate-300">{a.timeline}</Badge>
                          <Badge className={
                            a.impact === 'critical' ? 'bg-red-500/20 text-red-400' :
                            a.impact === 'very_high' ? 'bg-orange-500/20 text-orange-400' :
                            a.impact === 'high' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-500/20 text-slate-400'
                          }>{a.impact}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Market Map + Opportunities */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-white">
                      <MapPin className="h-5 w-5 text-emerald-400" />
                      Northern Virginia Market Map
                      <Badge variant="outline" className="border-slate-600 text-slate-400">{growthData.market?.total_65_plus?.toLocaleString()} 65+ population</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {growthData.market?.counties?.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                          <div>
                            <p className="text-white font-medium">{c.name}</p>
                            <p className="text-xs text-slate-400">65+: {c.population_65_plus?.toLocaleString()} | Growth: {c.growth_rate}</p>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              c.opportunity === 'very_high' ? 'bg-red-500/20 text-red-400' :
                              c.opportunity === 'high' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-slate-500/20 text-slate-400'
                            }>{c.hospice_penetration} penetration</Badge>
                            <p className="text-xs text-slate-500 mt-1">{c.notes}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Growth Opportunities */}
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-white">
                      <TrendingUp className="h-5 w-5 text-amber-400" />
                      Top Growth Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {growthData.opportunities?.opportunities?.filter(o => o.type === 'geographic_expansion').slice(0, 5).map((o, i) => (
                        <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-white font-medium text-sm">{o.name}</p>
                            <Badge className="bg-emerald-500/20 text-emerald-400">+{o.potential_new_patients} patients</Badge>
                          </div>
                          <p className="text-xs text-slate-400">Gap: {o.gap_pct} | Priority: P{o.priority} | Growth: {o.growth_rate}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Partner Database */}
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Users className="h-5 w-5 text-purple-400" />
                    Referral Partner Database
                    <Badge variant="outline" className="border-slate-600 text-slate-400">{growthData.partners?.total_partners} partners identified</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(growthData.partners?.categories || {}).map(([catKey, cat]) => (
                    <div key={catKey} className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-semibold">{cat.label}</h4>
                        <Badge className="bg-purple-500/20 text-purple-400">{cat.priority} — Target Year {cat.target_year}</Badge>
                      </div>
                      <div className="space-y-1">
                        {cat.partners?.map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-800/30 hover:bg-slate-800/60 transition-colors">
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-white">{p.name}</span>
                              {p.location && <span className="text-xs text-slate-500 ml-2">— {p.location}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={
                                p.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                                p.priority === 'high' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-slate-500/20 text-slate-400'
                              }>{p.priority}</Badge>
                              <Badge className={
                                p.referral_potential === 'very_high' ? 'bg-emerald-500/20 text-emerald-400' :
                                p.referral_potential === 'high' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-500/20 text-slate-400'
                              }>{p.referral_potential}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Growth Intelligence Modules — Upload & Analyze */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Referral Scorecarding */}
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-white text-base">
                      <BarChart3 className="h-5 w-5 text-emerald-400" />
                      Referral Scorecarding
                    </CardTitle>
                    <p className="text-xs text-slate-400">Upload referral data → A/B/C/D tier scoring</p>
                  </CardHeader>
                  <CardContent>
                    <label className="block w-full p-4 border-2 border-dashed border-slate-600 rounded-lg text-center cursor-pointer hover:border-emerald-500/50 transition-colors">
                      <Upload className="h-6 w-6 mx-auto mb-1 text-slate-500" />
                      <p className="text-xs text-slate-400">Upload Referral CSV</p>
                      <input type="file" className="hidden" accept=".csv,.xlsx" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const formData = new FormData(); formData.append('file', f);
                        try {
                          const res = await fetch(`${API_URL}/api/growth-intel/referral-scorecard`, {
                            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
                          });
                          const data = await res.json();
                          if (data.scorecards) {
                            setChatMessages(prev => [...prev,
                              { role: 'user', content: `Uploaded referral data: ${f.name}`, timestamp: new Date().toISOString() },
                              { role: 'assistant', content: `REFERRAL SCORECARD ANALYSIS\n\n${data.summary.total_sources} sources analyzed:\n- A-Tier (protect & grow): ${data.summary.a_tier}\n- B-Tier (invest): ${data.summary.b_tier}\n- C-Tier (develop): ${data.summary.c_tier}\n- D-Tier (evaluate): ${data.summary.d_tier}\n\nOverall conversion: ${data.summary.avg_conversion}%\nTotal revenue: $${data.summary.total_revenue?.toLocaleString()}\n\nTop sources:\n${data.scorecards.slice(0,5).map(s => `• ${s.source}: ${s.tier}-tier | ${s.referrals} refs → ${s.admissions} admits (${s.conversion_rate}%) | $${s.revenue?.toLocaleString()} | ${s.action}`).join('\n')}`, timestamp: new Date().toISOString(), canExport: true }
                            ]);
                          }
                        } catch { alert('Upload failed'); }
                        e.target.value = '';
                      }} />
                    </label>
                    <p className="text-xs text-slate-500 mt-2">Columns: Source, Referrals, Admissions, Avg LOS, Revenue</p>
                  </CardContent>
                </Card>

                {/* Physician Pattern Tracker */}
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-white text-base">
                      <Users className="h-5 w-5 text-amber-400" />
                      Physician Patterns
                    </CardTitle>
                    <p className="text-xs text-slate-400">Identify late vs appropriate referrers</p>
                  </CardHeader>
                  <CardContent>
                    <label className="block w-full p-4 border-2 border-dashed border-slate-600 rounded-lg text-center cursor-pointer hover:border-amber-500/50 transition-colors">
                      <Upload className="h-6 w-6 mx-auto mb-1 text-slate-500" />
                      <p className="text-xs text-slate-400">Upload Physician CSV</p>
                      <input type="file" className="hidden" accept=".csv,.xlsx" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const formData = new FormData(); formData.append('file', f);
                        try {
                          const res = await fetch(`${API_URL}/api/growth-intel/physician-patterns`, {
                            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
                          });
                          const data = await res.json();
                          if (data.physicians) {
                            setChatMessages(prev => [...prev,
                              { role: 'user', content: `Uploaded physician data: ${f.name}`, timestamp: new Date().toISOString() },
                              { role: 'assistant', content: `PHYSICIAN REFERRAL PATTERN ANALYSIS\n\n${data.summary.total_physicians} physicians analyzed:\n- Crisis referrers (≤7 day LOS): ${data.summary.crisis_referrers} ⚠️\n- Late referrers (8-14 days): ${data.summary.late_referrers}\n- Appropriate referrers (15-45 days): ${data.summary.appropriate_referrers} ✓\n- Early/ideal referrers (45+ days): ${data.summary.early_referrers} ★\n\nAverage LOS at referral: ${data.summary.avg_los_all} days\n\nPRIORITY EDUCATION TARGETS:\n${data.summary.education_priority?.map(p => `• ${p}`).join('\n') || 'None identified'}\n\nDETAILS:\n${data.physicians.slice(0,8).map(p => `• Dr. ${p.physician}: ${p.avg_los} days avg LOS | ${p.total_referrals} refs | ${p.label}\n  Action: ${p.education_plan}`).join('\n\n')}`, timestamp: new Date().toISOString(), canExport: true }
                            ]);
                          }
                        } catch { alert('Upload failed'); }
                        e.target.value = '';
                      }} />
                    </label>
                    <p className="text-xs text-slate-500 mt-2">Columns: Physician, Referrals, Avg LOS, Diagnosis</p>
                  </CardContent>
                </Card>

                {/* Competitive Intel */}
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-white text-base">
                      <Target className="h-5 w-5 text-red-400" />
                      Competitive Intel
                    </CardTitle>
                    <p className="text-xs text-slate-400">Know your competitors, win the market</p>
                  </CardHeader>
                  <CardContent>
                    <label className="block w-full p-4 border-2 border-dashed border-slate-600 rounded-lg text-center cursor-pointer hover:border-red-500/50 transition-colors">
                      <Upload className="h-6 w-6 mx-auto mb-1 text-slate-500" />
                      <p className="text-xs text-slate-400">Upload Competitor CSV</p>
                      <input type="file" className="hidden" accept=".csv,.xlsx" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const formData = new FormData(); formData.append('file', f);
                        try {
                          const res = await fetch(`${API_URL}/api/growth-intel/competitive-intel`, {
                            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
                          });
                          const data = await res.json();
                          if (data.competitors) {
                            setChatMessages(prev => [...prev,
                              { role: 'user', content: `Uploaded competitive data: ${f.name}`, timestamp: new Date().toISOString() },
                              { role: 'assistant', content: `COMPETITIVE INTELLIGENCE ANALYSIS\n\n${data.summary.total_competitors} competitors analyzed:\n${data.competitors.map(c => `• ${c.name}: Quality ${c.quality_score} | Share: ${c.market_share} | ${c.counties}\n  Strengths: ${c.strengths}\n  Weaknesses: ${c.weaknesses}`).join('\n\n')}`, timestamp: new Date().toISOString(), canExport: true }
                            ]);
                          }
                        } catch { alert('Upload failed'); }
                        e.target.value = '';
                      }} />
                    </label>
                    <button onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/growth-intel/competitive-landscape`, { headers: { Authorization: `Bearer ${token}` } });
                        const data = await res.json();
                        setChatMessages(prev => [...prev,
                          { role: 'user', content: 'Show me the NOVA competitive landscape', timestamp: new Date().toISOString() },
                          { role: 'assistant', content: `NORTHERN VIRGINIA COMPETITIVE LANDSCAPE\n\nYOUR POSITION:\n${data.your_position?.differentiators?.map(d => `✓ ${d}`).join('\n')}\n\nCOMPETITORS:\n${data.competitors?.map(c => `\n${c.name} (est. ADC: ${c.est_adc})\n  Quality: ${c.quality_stars}★ | Area: ${c.counties}\n  Strengths: ${c.strengths}\n  Weaknesses: ${c.weaknesses}\n  YOUR STRATEGY: ${c.strategy}`).join('\n')}`, timestamp: new Date().toISOString(), canExport: true }
                        ]);
                      } catch { alert('Failed to load'); }
                    }} className="w-full mt-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-xs hover:bg-red-500/20 transition-colors">
                      View NOVA Competitive Landscape
                    </button>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin opacity-50" />
              <p>Loading growth & market strategy data...</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
